/**
 * Polish public holidays and Sunday detection for mileage audit warnings.
 * Poland has 14 public holidays (from 2025): 10 fixed-date + 4 Easter-based moveable.
 */

/** Meeus/Jones/Butcher algorithm for Easter Sunday date */
function getEasterDate(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function formatDateKey(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

/** Returns a map of YYYY-MM-DD -> Polish holiday name for the given year */
export function getPolishHolidays(year: number): Map<string, string> {
  const holidays = new Map<string, string>();

  // Fixed-date holidays
  holidays.set(`${year}-01-01`, 'Nowy Rok');
  holidays.set(`${year}-01-06`, 'Trzech Króli');
  holidays.set(`${year}-05-01`, 'Święto Pracy');
  holidays.set(`${year}-05-03`, 'Święto Konstytucji 3 Maja');
  holidays.set(`${year}-08-15`, 'Wniebowzięcie NMP');
  holidays.set(`${year}-11-01`, 'Wszystkich Świętych');
  holidays.set(`${year}-11-11`, 'Święto Niepodległości');
  if (year >= 2025) {
    holidays.set(`${year}-12-24`, 'Wigilia');
  }
  holidays.set(`${year}-12-25`, 'Boże Narodzenie');
  holidays.set(`${year}-12-26`, 'Drugi dzień Bożego Narodzenia');

  // Easter-based moveable holidays
  const easter = getEasterDate(year);
  holidays.set(formatDateKey(easter), 'Wielkanoc');
  holidays.set(formatDateKey(addDays(easter, 1)), 'Poniedziałek Wielkanocny');
  holidays.set(formatDateKey(addDays(easter, 49)), 'Zesłanie Ducha Świętego');
  holidays.set(formatDateKey(addDays(easter, 60)), 'Boże Ciało');

  return holidays;
}

export function isSunday(dateStr: string): boolean {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).getDay() === 0;
}

/**
 * Returns a warning string if the date is a Sunday and/or a Polish public holiday.
 * Returns null if no warning needed.
 */
export function getTripDateWarning(dateStr: string): string | null {
  if (!dateStr || dateStr.length < 10) return null;

  const year = parseInt(dateStr.substring(0, 4), 10);
  if (isNaN(year)) return null;

  const holidays = getPolishHolidays(year);
  const sunday = isSunday(dateStr);
  const holidayName = holidays.get(dateStr);

  if (sunday && holidayName) return `Niedziela / ${holidayName}`;
  if (sunday) return 'Niedziela';
  if (holidayName) return holidayName;
  return null;
}
